'use client';

import Image from 'next/image';
import Link from 'next/link';
import { useParams } from 'next/navigation';

export type PhotoCard = {
  image: string;
  title: string;
  description?: string | null;
  desc?: string | null;
  link_url?: string | null;
  link_text?: string | null;
};

type PhotoCardGridSectionProps = {
  heading?: string;
  cards: PhotoCard[];
  columns?: number;
  add_background?: boolean;
  add_bottom_border?: boolean;
};

export default function PhotoCardGridSection({
  heading,
  cards,
  columns = 3,
  add_background = false,
  add_bottom_border = false,
}: PhotoCardGridSectionProps) {
  const params = useParams();
  const lang = params.lang as string;

  const gridCols: Record<number, string> = {
    1: 'grid-cols-1',
    2: 'grid-cols-1 md:grid-cols-2',
    3: 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3',
    4: 'grid-cols-1 md:grid-cols-2 lg:grid-cols-4',
    5: 'grid-cols-1 md:grid-cols-3 lg:grid-cols-5',
    6: 'grid-cols-1 md:grid-cols-3 lg:grid-cols-6',
  };
  const gridClass = gridCols[columns] ?? gridCols[3];

  const content = (
    <>
      {heading && (
        <h2 className="text-3xl font-bold text-primary-700 mb-4">{heading}</h2>
      )}
      <div className={`grid ${gridClass} gap-6 w-full ${add_bottom_border ? 'pb-8 border-b border-gray-200' : ''}`}>
        {cards.map((card, i) => {
          const linkHref = card.link_url
            ? card.link_url.startsWith('http') || card.link_url.startsWith('/')
              ? card.link_url
              : `/${lang}/${card.link_url}`
            : undefined;
          const descriptionHtml = card.description ?? card.desc;

          const cardContent = (
            <div className="flex flex-col rounded-2xl overflow-hidden bg-white shadow-md hover:shadow-lg transition-shadow h-full">
              {/* Image */}
              <div className="relative w-full aspect-video bg-gray-100">
                {card.image ? (
                  <Image
                    src={card.image}
                    alt={card.title}
                    fill
                    className="object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-gray-400 text-sm">
                    No image
                  </div>
                )}
              </div>

              {/* Body */}
              <div className="flex flex-col gap-3 p-6 flex-1">
                <h3 className="text-lg font-bold text-gray-900 leading-snug">
                  {card.title}
                </h3>
                {descriptionHtml && (
                  <div
                    className="text-gray-600 text-[18px] leading-relaxed flex-1 [&_p]:mb-2 [&_p:last-child]:mb-0"
                    dangerouslySetInnerHTML={{ __html: descriptionHtml }}
                  />
                )}
              </div>

              {/* CTA */}
              {card.link_text && (
                <div className="px-6 pb-6">
                  <span className="inline-block bg-primary-700 hover:bg-primary-800 text-white font-semibold text-sm px-6 py-3 rounded-lg transition-colors cursor-pointer">
                    {card.link_text}
                  </span>
                </div>
              )}
            </div>
          );

          return linkHref ? (
            <Link key={i} href={linkHref} className="flex">
              {cardContent}
            </Link>
          ) : (
            <div key={i} className="flex">
              {cardContent}
            </div>
          );
        })}
      </div>
    </>
  );

  if (add_background) {
    return (
      <section className="w-screen relative left-1/2 right-1/2 -mx-[40vw] bg-[linear-gradient(to_bottom,rgba(237,237,249,0),#DEE5E8)] mb-8 py-8">
        <div className="container max-w-7xl mx-auto px-10">
          {content}
        </div>
      </section>
    );
  }

  return <div className="w-full">{content}</div>;
}
